import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, onValue, off, getDatabase } from 'firebase/database';
import app from '../config/firebase';
import './GamePage.css';

const database = getDatabase(app);

function GamePage({ API_URL }) {
  const { code } = useParams();
  const navigate = useNavigate();

  const [timeLeft, setTimeLeft] = useState(30);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votingInProgress, setVotingInProgress] = useState(false);
  const [roundStartTime, setRoundStartTime] = useState(null);
  const VOTING_DURATION = 60; // Seconds for voting

  const [isHost, setIsHost] = useState(false);
  
  const [gameState, setGameState] = useState({
    currentQuestion: "Who is most likely to become a millionaire?",
    players: [
      { id: 1, name: 'John', hasVoted: false, votes: 2 },
      { id: 2, name: 'Alice', hasVoted: true, votes: 1 },
      { id: 3, name: 'Bob', hasVoted: false, votes: 0 },
      { id: 4, name: 'Emma', hasVoted: true, votes: 1 },
    ]
  });

  // Listen for real-time game state updates
  useEffect(() => {
    const partyRef = ref(database, `parties/${code}`);
    let previousRoundNumber = null;
    
    // Set up real-time listener
    onValue(partyRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setError('Party not found');
        setIsLoading(false);
        return;
      }

      if (data.gameFinished) {
        navigate(`/analysis/${code}`);
        return;
      }

      if (!data.gameStarted && !data.gameFinished) {
        navigate(`/lobby/${code}`);
        return;
      }

      console.log('Game state:', data);

      // Check if question has changed
      if (previousRoundNumber !== null && previousRoundNumber !== data.currentRound) {
        setShowResults(false);
        setTimeLeft(30);
        setSelectedPlayer(null);
      }
      previousRoundNumber = data.currentRound;

      // Update round start time if available
      if (data.roundStartTime) {
        setRoundStartTime(data.roundStartTime);
        
        // Calculate remaining time based on server timestamp
        const currentTime = Date.now();
        const elapsedSeconds = Math.floor((currentTime - data.roundStartTime) / 1000);
        const remainingTime = Math.max(0, VOTING_DURATION - elapsedSeconds);
        
        // If time is already up but results aren't shown, show them
        if (remainingTime <= 0 && !showResults) {
          setTimeLeft(0);
          setShowResults(true);
        } else if (!showResults) {
          // Only update the timer if we're not already showing results
          setTimeLeft(remainingTime);
        }
      }
      
      // Check if all players have voted to show results early
      const players = Object.entries(data.players || {}).map(([id, player]) => ({
        id,
        name: player.name,
        hasVoted: player.hasVoted || false,
        votes: player.votes || 0,
        totalVotesReceived: player.totalVotesReceived || 0
      }));
      
      // If everyone has voted and we're not showing results yet, show them
      const allPlayersVoted = players.length > 0 && players.every(player => player.hasVoted);
      if (allPlayersVoted && !showResults && timeLeft > 0) {
        console.log('All players have voted, showing results early');
        setShowResults(true);
      }
      
      setGameState({
        currentQuestion: data.currentQuestion,
        currentRound: data.currentRound,
        players: Object.entries(data.players || {}).map(([id, player]) => ({
          id,
          name: player.name,
          hasVoted: player.hasVoted || false,
          votes: player.votes || 0,
          totalVotesReceived: player.totalVotesReceived || 0
        }))
      });
      setIsHost(data.info?.hostId === localStorage.getItem('playerId'));
      setError(null);
      setIsLoading(false);
    });

    // Clean up listener when component unmounts
    return () => {
      off(partyRef);
    };
  }, [code, navigate]);

  // Timer effect - synchronized with server timestamp
  useEffect(() => {
    if (roundStartTime && timeLeft > 0 && !showResults) {
      const timer = setTimeout(() => {
        // Recalculate time based on server timestamp on each tick
        const currentTime = Date.now();
        const elapsedSeconds = Math.floor((currentTime - roundStartTime) / 1000);
        const remainingTime = Math.max(0, VOTING_DURATION - elapsedSeconds);
        
        setTimeLeft(remainingTime);
        
        if (remainingTime <= 0) {
          setShowResults(true);
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [timeLeft, showResults, roundStartTime]);

  const getPlayerInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const handleVote = async (playerId) => {
    if (timeLeft > 0 && !selectedPlayer && !gameState.players.find(player => player.id === localStorage.getItem('playerId')).hasVoted && !votingInProgress) {
      setVotingInProgress(true);
      try {
        const response = await fetch(`${API_URL}/parties/${code}/vote`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            playerId: localStorage.getItem('playerId'),
            votedForId: playerId,
            roundId: gameState.currentRound
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setSelectedPlayer(playerId);
      } catch (err) {
        console.error('Error submitting vote:', err);
        setError('Failed to submit vote. Please try again.');
      }
    }
    setVotingInProgress(false);
  };

  const handleFinishGame = async () => {
    setIsLoading(true);
    if (!isHost) {
      setError('Only the host can finish the game');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/parties/${code}/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      navigate(`/analysis/${code}`);
    } catch (err) {
      console.error('Error finishing game:', err);
      setError('Failed to finish game. Please try again.');
    }
    setIsLoading(false);
  };

  const handleNextQuestion = async () => {
    setIsLoading(true);
    if (!isHost) {
      setError('Only the host can move to the next question');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/parties/${code}/next-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // The roundStartTime will be updated via Firebase listener
      // so we don't need to set it manually here
      setShowResults(false);
      setSelectedPlayer(null);
      setError(null);
      // Don't manually set timeLeft as it will be calculated from the server timestamp
    } catch (err) {
      console.error('Error moving to next question:', err);
      setError('Failed to move to next question. Please try again.');
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="game-container">
        <div className="game-content">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-container">
        <div className="game-content">
          <div className="error-message">{error}</div>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <div className="game-content">
        <div className="question-card">
          {!showResults ? <div className="timer">{timeLeft}s</div> : null}
          <h2 className="question-text">{gameState.currentQuestion}</h2>
          
          {!showResults ? (
            <div className="players-grid">
              {gameState.players.map(player => (
                <div
                  key={player.id}
                  className={`player-card ${selectedPlayer === player.id ? 'selected' : ''}`}
                  onClick={() => handleVote(player.id)}
                >
                  <div className="player-avatar">
                    {getPlayerInitials(player.name)}
                  </div>
                  <div className="player-name">{player.name}</div>
                  <div className="vote-status">
                    {player.hasVoted ? 'Voted' : 'Thinking...'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="results-grid">
                {gameState.players
                  .sort((a, b) => b.votes - a.votes)
                  .map((player, index) => (
                    <div
                      key={player.id}
                      className={`result-card ${index === 0 ? 'winner' : ''}`}
                    >
                      <div className="result-avatar">
                        {getPlayerInitials(player.name)}
                      </div>
                      <div className="result-name">{player.name}</div>
                      <div className="vote-count">{player.votes}</div>
                      <div className="vote-label">
                        {player.votes === 1 ? 'vote' : 'votes'}
                      </div>
                    </div>
                  ))}
              </div>
              {showResults && isHost && (
                <div className="host-controls">
                  <button className="finish-button" onClick={handleFinishGame}>
                    Finish & Analyze Game
                  </button>
                  <button className="next-button" onClick={handleNextQuestion}>
                    Next Question
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GamePage;
